import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plane, Send, Trash2, Ticket, Receipt, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { api, fileUrl } from "../lib/api";
import { FlightBookingDialog } from "./FlightBookingDialog";

const CLS = { draft: "bg-slate-100 text-slate-600", sent: "bg-sky-100 text-sky-700", booked: "bg-indigo-100 text-indigo-700", paid: "bg-emerald-100 text-emerald-700", cancelled: "bg-rose-100 text-rose-600" };

const FileSlot = ({ b, kind, onChanged }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const isT = kind === "ticket", id = b[`${kind}_id`], Icon = isT ? Ticket : Receipt;
  const up = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const u = await api.uploadFile(file);
      if (isT) {
        const r = await api.flightTicket(b.id, u.attachment_id);
        if (r.notified) toast.success("تم رفع التذكرة وإرسالها للموظف على واتساب ✈️");
        else toast.warning(`تم رفع التذكرة — لم يُرسل واتساب: ${r.reason || ""}. تظهر للموظف في بوابته`);
      } else { await api.flightReceipt(b.id, u.attachment_id); toast.success("تم رفع إيصال السداد"); }
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الرفع"); } finally { setBusy(false); }
  };
  return (
    <span className="inline-flex items-center gap-1">
      <input ref={ref} type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => { up(e.target.files?.[0]); e.target.value = ""; }} />
      {id && <a href={fileUrl(id)} target="_blank" rel="noreferrer" title={b[`${kind}_name`]} className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold ${isT ? "bg-indigo-50 text-indigo-700" : "bg-emerald-50 text-emerald-700"}`} data-testid={`flight-${kind}-view-${b.id}`}><Icon className="h-3.5 w-3.5" /> {isT ? "التذكرة" : "الإيصال"}</a>}
      <button type="button" disabled={busy} onClick={() => ref.current?.click()} title={id ? (isT ? "استبدال التذكرة" : "استبدال الإيصال") : isT ? "رفع التذكرة (تُرسل للموظف)" : "رفع إيصال السداد"} aria-label={id ? "استبدال الملف" : "رفع الملف"} data-testid={`flight-${kind}-upload-${b.id}`}
        className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-[11px] font-semibold ${isT ? "border-indigo-200 text-indigo-700 hover:bg-indigo-50" : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"}`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : id ? <Upload className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />} {id ? "" : isT ? "رفع التذكرة" : "رفع الإيصال"}</button>
    </span>
  );
};

const Row = ({ b, statuses, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const run = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التنفيذ"); } finally { setBusy(false); } };
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-3 py-2.5 last:border-0" data-testid={`flight-row-${b.id}`}>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-slate-800">{b.employee_name} <span className="text-xs font-normal text-slate-400">#{b.number}</span></p>
        <p className="truncate text-xs text-slate-500">{b.from_city || "—"} ← {b.to_city || "—"} · {b.depart_date}{b.return_date ? ` ↔ ${b.return_date}` : " (ذهاب)"} · {b.travelers?.length} مسافر</p>
      </div>
      <select value={b.status} disabled={busy} onChange={(e) => run(() => api.flightStatus(b.id, e.target.value), "تم تحديث الحالة")}
        className={`h-8 rounded-full border-0 px-2 text-xs font-bold ${CLS[b.status]}`} data-testid={`flight-status-${b.id}`}>
        {Object.entries(statuses).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      {b.status !== "cancelled" && <FileSlot b={b} kind="ticket" onChanged={onChanged} />}
      {b.status !== "cancelled" && <FileSlot b={b} kind="receipt" onChanged={onChanged} />}
      {b.status === "draft" && <Button size="sm" disabled={busy} onClick={() => run(() => api.sendFlight(b.id), "تم الإرسال لفريق المسافر")} className="h-8 gap-1 bg-sky-600 hover:bg-sky-700" data-testid={`flight-row-send-${b.id}`}><Send className="h-3.5 w-3.5" /> إرسال</Button>}
      {["draft", "cancelled"].includes(b.status) && <button disabled={busy} onClick={() => window.confirm("حذف الطلب؟") && run(() => api.deleteFlight(b.id), "تم الحذف")} className="rounded p-1.5 text-rose-500 hover:bg-rose-50" data-testid={`flight-row-delete-${b.id}`}><Trash2 className="h-4 w-4" /></button>}
    </div>
  );
};

// Header button: new booking + history of booking requests
export const FlightBookingsButton = () => {
  const [open, setOpen] = useState(false);
  const [neo, setNeo] = useState(false);
  const [d, setD] = useState(null);
  const load = () => api.listFlights().then(setD).catch(() => toast.error("تعذّر تحميل الحجوزات"));
  useEffect(() => { if (open) load(); }, [open]);
  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)} className="h-8 gap-1.5 bg-sky-600 hover:bg-sky-700" data-testid="flight-bookings-btn"><Plane className="h-4 w-4" /> حجز تذكرة طيران</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="rtl" className="w-[96vw] max-w-2xl max-h-[90dvh] overflow-y-auto" data-testid="flight-bookings-dialog">
          <DialogHeader className="text-start"><DialogTitle className="flex items-center gap-2 ps-8"><Plane className="h-5 w-5 text-sky-600" /> حجوزات الطيران</DialogTitle>
            <DialogDescription className="text-start">بالتعاون مع «المسافر» — تُرسل الطلبات لفريقهم ويصلك السعر والتذكرة ورابط السداد.</DialogDescription></DialogHeader>
          <Button onClick={() => setNeo(true)} className="w-full gap-1.5 bg-sky-600 hover:bg-sky-700" data-testid="flight-new-btn"><Plane className="h-4 w-4" /> طلب حجز جديد</Button>
          <div className="overflow-hidden rounded-xl border border-slate-200">
            {!d ? <Loader2 className="mx-auto my-8 h-6 w-6 animate-spin text-sky-600" />
              : d.items.length === 0 ? <p className="p-8 text-center text-sm text-slate-400" data-testid="flight-empty">لا توجد طلبات حجز بعد</p>
                : d.items.map((b) => <Row key={b.id} b={b} statuses={d.statuses} onChanged={load} />)}
          </div>
        </DialogContent>
      </Dialog>
      <FlightBookingDialog open={neo} onOpenChange={setNeo} onSaved={load} />
    </>
  );
};
